// Contexto de la API: construye el `ApiClient` con el JWT vigente y expone la
// superficie tipada (`Api`) a las vistas.

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { config } from '../config.js';
import { ApiClient } from './client.js';
import { createApi, type Api } from './endpoints.js';

const ApiContext = createContext<Api | null>(null);

/** Proveedor de la API. Requiere el getter del token y el callback de 401. */
export function ApiProvider({
  children,
  getToken,
  onUnauthorized,
}: {
  children: ReactNode;
  getToken: () => string | null;
  onUnauthorized: () => void;
}) {
  const api = useMemo(() => {
    const client = new ApiClient(config.apiUrl, getToken, onUnauthorized);
    return createApi(client);
  }, [getToken, onUnauthorized]);

  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
}

/** Hook para consumir la API tipada. */
export function useApi(): Api {
  const ctx = useContext(ApiContext);
  if (!ctx) throw new Error('useApi debe usarse dentro de <ApiProvider>.');
  return ctx;
}

/**
 * Variante NO lanzadora: devuelve la API o `null` si no hay `<ApiProvider>` en
 * el árbol (p. ej. en la capa pública de marketing, sin sesión). Útil para
 * componentes que viven en ambas capas, como el selector de idioma.
 */
export function useApiMaybe(): Api | null {
  return useContext(ApiContext);
}
